class LocalStorageAsDB {
  /**
   * Constructor for LocalStorageAsDB
   * @param storageKey - The key under which data will be stored in localStorage
   */
  constructor(storageKey) {
    this.storageKey = storageKey;
  }

  /**
   * Get all items from storage
   * @returns Object containing all stored items or empty object if none exists
   */
  getAll() {
    try {
      const data = localStorage.getItem(this.storageKey);
      return data ? JSON.parse(data) : {};
    } catch (error) {
      console.error('Failed to get data from localStorage:', error);
      return {};
    }
  }

  /**
   * Get a specific item by ID
   * @param id - The ID of the item to retrieve
   * @returns The requested item or null if not found
   */
  getById(id) {
    const data = this.getAll();
    return data[id] || null;
  }

  /**
   * Create or update an item
   * @param id - The ID of the item
   * @param item - The item data
   * @returns data if successful, null otherwise
   */
  create(id, item) {
    try {
      const data = this.getAll();
      data[id] = item;
      localStorage.setItem(this.storageKey, JSON.stringify(data));
      return data;
    } catch (error) {
      console.error('Failed to create item in localStorage:', error);
      return null;
    }
  }

  /**
   * Update an existing item or specific properties of it
   * @param id - The ID of the item to update
   * @param updates - Object containing the item updated
   * @returns data with the element updated if successful, null if item doesn't exist or operation failed
   */
  update(id, updates) {
    try {
      const data = this.getAll();
      if (!data[id]) return null;

      data[id] = updates;
      localStorage.setItem(this.storageKey, JSON.stringify(data));
      return data;
    } catch (error) {
      console.error('Failed to update item in localStorage:', error);
      return null;
    }
  }

  /**
   * Delete an item by ID
   * @param id - The ID of the item to delete
   * @returns data without the item if successful, null otherwise
   */
  delete(id) {
    try {
      const data = this.getAll();
      if (!data[id]) return false;

      delete data[id];
      localStorage.setItem(this.storageKey, JSON.stringify(data));
      return data;
    } catch (error) {
      console.error('Failed to delete item from localStorage:', error);
      return null;
    }
  }

  /**
   * Clear all data stored under this key
   * @returns True if successful, false otherwise
   */
  clear() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify({}));
      return true;
    } catch (error) {
      console.error('Failed to clear data in localStorage:', error);
      return false;
    }
  }
}

export default LocalStorageAsDB;
